/**
 * VAG-group ECU diagnostic-address table for the multi-ECU DTC scan.
 *
 * Maps each module's VAG "logical diagnostic address" (the 2-digit hex used
 * by VCDS, OBDeleven, Carista and other VAG diagnostic tools) to:
 *   - Human-readable name (e.g. "Engine electronics", "Adaptive Cruise Control")
 *   - CAN 11-bit TX/RX header pair for UDS over ISO-TP
 *
 * Coverage: VAG-group BEV / ICE / Hybrid post-2008 (MQB / MLB / MEB / J1
 * platforms). Pre-2008 PQ34 / PQ35 platforms use KWP1281 over K-Line which a
 * stock ELM327 cannot speak — those are out of scope for Phase 1 even though
 * the diagnostic addresses themselves are the same.
 *
 * Sources (in citation order):
 *   - https://github.com/ConnorHowell/vag-uds-ids — extracted from ODIS
 *     (VAG's official factory tool) covering MLB platform
 *   - https://vagcode.info/en/components — community database, cross-checked
 *     per-address across all 6 VAG brands
 *   - Ross-Tech VCDS published AUTOSCAN.TXT (per-chassis polling lists)
 *     https://static.elitesecurity.org/uploads/3/1/3148049/autoscan%20vcds%202012.txt
 *
 * The 11-bit TX/RX pair follows the **VAG TX→RX = TX + 0x6A** convention
 * for non-emissions ECUs. The two exceptions are address `01` (Engine,
 * `7E0`/`7E8`) and address `02` (Auto Transmission, `7E1`/`7E9`) which use
 * the SAE J1979 standard pair inherited from the original OBD-II spec —
 * the same pair the standard Mode 03/07/0A reader already targets via the
 * `0x7DF` broadcast. They're listed here for completeness but the
 * multi-ECU scanner deliberately skips them (covered by the emissions
 * path). Pre-derived here rather than computed at runtime so the lookup
 * is a single Map.get() — important because the scanner iterates the
 * table once per scan and we don't want allocator churn.
 */

export interface VagEcuAddress {
  /** Two-digit hex logical address as VAG / VCDS / Carista display it
   * (e.g. "13" for Adaptive Cruise Control). Lowercase. */
  address: string;
  /** Human-readable module name. Display label in the report + dashboard. */
  name: string;
  /** 11-bit CAN TX header (lowercase 3-hex-digit string, no prefix). */
  canTxId: string;
  /** 11-bit CAN RX header (lowercase 3-hex-digit string, no prefix). */
  canRxId: string;
}

// Core post-2008 VAG diagnostic addresses. List is INTENTIONALLY not
// exhaustive — see Ross-Tech AUTOSCAN.TXT for the ~50-module-deep blind
// probe lists per chassis (4G = C7 A6, 3G = B9 A4, 8V = MK3 A3 etc.). The
// addresses kept here cover (a) every module Carista's standard scan touches
// AND (b) every module the bundled OBDb signal sets already reference. New
// addresses can be added without code changes as long as the TX/RX pair is
// the standard TX + 0x6A.
//
// Address-formatting convention: the LOGICAL diagnostic address is the
// canonical identifier (it's what VCDS / Carista / OBDeleven all show); the
// CAN header pair is derived per the post-2008 VAG TP/UDS convention. Do
// NOT reorder this list — the runner iterates it sequentially when the
// gateway installation list is unavailable (fallback blind probe).
export const VAG_ECU_ADDRESSES: VagEcuAddress[] = [
  { address: '01', name: 'Engine electronics', canTxId: '7e0', canRxId: '7e8' },
  { address: '02', name: 'Auto transmission', canTxId: '7e1', canRxId: '7e9' },
  { address: '03', name: 'ABS / ESP brakes', canTxId: '713', canRxId: '77d' },
  { address: '08', name: 'Auto HVAC / Climatronic', canTxId: '746', canRxId: '7b0' },
  { address: '09', name: 'Central electronics (BCM)', canTxId: '70e', canRxId: '778' },
  { address: '13', name: 'Adaptive Cruise Control', canTxId: '757', canRxId: '7c1' },
  { address: '14', name: 'Suspension electronics', canTxId: '755', canRxId: '7bf' },
  { address: '15', name: 'Airbags', canTxId: '715', canRxId: '77f' },
  { address: '17', name: 'Instrument cluster', canTxId: '714', canRxId: '77e' },
  { address: '19', name: 'CAN Gateway (J533)', canTxId: '710', canRxId: '77a' },
  { address: '1a', name: 'Rear climate control', canTxId: '71a', canRxId: '784' },
  { address: '22', name: 'All-wheel-drive electronics', canTxId: '70f', canRxId: '779' },
  { address: '2b', name: 'Steering column assist', canTxId: '712', canRxId: '77c' },
  { address: '37', name: 'Navigation', canTxId: '733', canRxId: '79d' },
  { address: '3c', name: 'Lane assist', canTxId: '75a', canRxId: '7c4' },
  { address: '42', name: 'Door electronics (driver)', canTxId: '74f', canRxId: '7b9' },
  { address: '44', name: 'Power steering', canTxId: '712', canRxId: '77c' },
  { address: '46', name: 'Comfort control', canTxId: '746', canRxId: '7b0' },
  { address: '52', name: 'Door electronics (passenger)', canTxId: '750', canRxId: '7ba' },
  { address: '53', name: 'Parking brake (electronic)', canTxId: '74b', canRxId: '7b5' },
  { address: '55', name: 'Headlight range / Xenon', canTxId: '732', canRxId: '79c' },
  { address: '56', name: 'Radio', canTxId: '714', canRxId: '77e' },
  { address: '5f', name: 'Information electronics (MIB)', canTxId: '773', canRxId: '7dd' },
  { address: '62', name: 'Rear left door', canTxId: '73e', canRxId: '7a8' },
  { address: '65', name: 'Tyre pressure monitoring', canTxId: '70b', canRxId: '775' },
  { address: '69', name: 'Trailer module', canTxId: '747', canRxId: '7b1' },
  { address: '6c', name: 'Rear-view camera', canTxId: '74e', canRxId: '7b8' },
  { address: '6d', name: 'Trunk electronics', canTxId: '74c', canRxId: '7b6' },
  { address: '72', name: 'Rear right door', canTxId: '751', canRxId: '7bb' },
  { address: '75', name: 'Telematics / emergency call', canTxId: '767', canRxId: '7d1' },
  { address: '76', name: 'Park assist', canTxId: '70a', canRxId: '774' },
  { address: '77', name: 'Telephone', canTxId: '76b', canRxId: '7d5' },
];

// Pre-built lookup maps. Built once at module-load time so per-scan lookups
// are O(1) and don't iterate the array. Index by both `address` (for the
// gateway-installation-list path, which returns the logical address) and
// by `canTxId` (for response routing when a frame comes back).
const BY_ADDRESS = new Map<string, VagEcuAddress>(
  VAG_ECU_ADDRESSES.map((e) => [e.address.toLowerCase(), e]),
);
const BY_CAN_TX = new Map<string, VagEcuAddress>(
  VAG_ECU_ADDRESSES.map((e) => [e.canTxId.toLowerCase(), e]),
);

/**
 * Resolve a VAG logical diagnostic address (e.g. "13" or "0x13") to its
 * full ECU record. Returns undefined for unknown addresses — callers
 * should treat that as "unknown module, skip" rather than fabricating
 * fake names.
 */
export function lookupVagEcuByAddress(addressInput: string): VagEcuAddress | undefined {
  const normalised = addressInput.toLowerCase().replace(/^0x/, '').padStart(2, '0');
  return BY_ADDRESS.get(normalised);
}

/**
 * Reverse lookup: find which VAG ECU a CAN TX ID belongs to. Used by the
 * mode-19 response parser when grouping multi-ECU response frames back to
 * their source module.
 */
export function lookupVagEcuByCanTxId(canTxIdInput: string): VagEcuAddress | undefined {
  const normalised = canTxIdInput.toLowerCase().replace(/^0x/, '').padStart(3, '0');
  return BY_CAN_TX.get(normalised);
}

// VAG-group makes whose vehicles use the diagnostic-address conventions in
// this table. Lowercase, hyphen-normalised. Used by the mobile scanner to
// gate the multi-ECU DTC scan — non-VAG vehicles bypass the whole code path.
//
// Coverage rationale: all 6 brands ship vehicles built on the MQB / MLB /
// MEB platforms which share the J533 gateway architecture. Bentley + Lambo
// + Ducati are deliberately omitted (lower volume + materially different
// diagnostic stacks; if dealers need them we can add per-brand entries later).
const VAG_MAKES = new Set([
  'volkswagen',
  'vw',
  'audi',
  'skoda',
  'škoda',
  'seat',
  'cupra',
  'porsche',
]);

/**
 * True when the given make string belongs to the VAG group as defined above.
 * Case-insensitive, accent-insensitive (Škoda matches both styles), and
 * forgiving of leading / trailing whitespace.
 */
export function isVagMake(make: string | null | undefined): boolean {
  if (!make) return false;
  return VAG_MAKES.has(make.toLowerCase().trim());
}
